import { ShowDeviceMessage } from '@csp/common-message';
import { ZonedDateTimeFactory } from '@csp/csp-common-date-util';
import { Patient, ZonedDateTime } from '@csp/csp-common-model';
import {
  RequestActionDateUtil,
  RequestInitiationUtil,
  RequestUserStatus,
  RequestVersionStatus,
  RequestWindow,
  ScheduleVersionRequestRef,
  isWindowActiveNow,
} from '@csp/csp-common-scheduling';
import { ScheduleReferenceDateService } from '@csp/csp-common-patient-scheduling';
import { DeviceModelNumber } from '@csp/device-catalog';
import { DeviceObservationScheduleRequestRefV1 } from '@csp/dmdp-api-device-dto';
import { DeviceObservationRequest } from '../model/DeviceObservationRequest';
import { DeviceObservationRequestModelRefV1 } from '../model/DeviceObservationRequestModelRefV1';
import { DeviceObservationScheduleRequestInfo } from '../model/DeviceObservationScheduleRequestInfo';

const VISIBLE_DEVICE_REQUEST_VERSIONS_STATUSES: RequestVersionStatus[] = [
  RequestVersionStatus.ACTIVE,
  RequestVersionStatus.EXPIRED,
];
const VISIBLE_DEVICE_USER_REQUEST_STATUSES: RequestUserStatus[] = [
  RequestUserStatus.ONGOING,
  RequestUserStatus.DONE,
  RequestUserStatus.EXPIRED,
];

const isNotExpired = ({ requestVersionStatus }: DeviceObservationScheduleRequestInfo): boolean =>
  requestVersionStatus !== RequestVersionStatus.EXPIRED;

const hasVisibleStatus = ({ requestUserStatus, requestVersionStatus }: DeviceObservationScheduleRequestInfo): boolean =>
  VISIBLE_DEVICE_USER_REQUEST_STATUSES.includes(requestUserStatus) &&
  VISIBLE_DEVICE_REQUEST_VERSIONS_STATUSES.includes(requestVersionStatus);

const filterOnlyVisibleStatuses = (
  requests: DeviceObservationScheduleRequestInfo[],
): DeviceObservationScheduleRequestInfo[] => requests.filter(hasVisibleStatus);

const isRequestVersionActiveNow = ({ versionInfo }: DeviceObservationRequest): boolean =>
  isWindowActiveNow({ windowStart: versionInfo.activeFrom, windowEnd: versionInfo.activeTo });

const filterActiveNowRequests = (
  requests: DeviceObservationRequest[],
  patient: Patient,
  zonedNow = ZonedDateTimeFactory.now(),
): DeviceObservationRequest[] => {
  if (!requests) {
    return [];
  }

  const scheduleReferenceDates = ScheduleReferenceDateService.fromPatient(patient);
  return requests.filter(request => {
    const startActionTimestampWithOffset = RequestActionDateUtil.getStartActionOffsetTimestamp(
      request,
      scheduleReferenceDates,
    );
    if (startActionTimestampWithOffset && isRequestVersionActiveNow(request)) {
      const isStarted = startActionTimestampWithOffset.unixTimeMillis <= zonedNow.unixTimeMillis;
      const endActionTimestampWithOffset = RequestActionDateUtil.getEndActionOffsetTimestamp(
        request,
        scheduleReferenceDates,
      );
      if (endActionTimestampWithOffset) {
        const isEnded = zonedNow.unixTimeMillis >= endActionTimestampWithOffset.unixTimeMillis;
        return isStarted && !isEnded;
      } else {
        return isStarted;
      }
    } else {
      return false;
    }
  });
};

const filterRequestsWithSupportedDeviceModels = (requests: DeviceObservationRequest[]): DeviceObservationRequest[] =>
  requests.filter(({ referencedModelNumbers }) =>
    referencedModelNumbers.every(modelNumber => DeviceModelNumber[modelNumber]),
  );

const filterSchedulesRequests = (requests: DeviceObservationRequest[]): DeviceObservationRequest[] =>
  requests.filter(({ initiationConfig }) => RequestInitiationUtil.isInitiatedBySchedule(initiationConfig));

const filterOnDemandRequests = (requests: DeviceObservationRequest[]): DeviceObservationRequest[] =>
  requests.filter(({ initiationConfig }) => RequestInitiationUtil.isInitiatedOnDemand(initiationConfig));

const findByScheduleVersionRequestRef = (
  requests: DeviceObservationRequest[],
  scheduleVersionRequestRef: ScheduleVersionRequestRef,
): DeviceObservationRequest | undefined =>
  requests.find(request => ScheduleVersionRequestRef.isSameRequestRef(request, scheduleVersionRequestRef));

const isWindowPassed = (
  requestWindow: RequestWindow,
  zonedDateTime: ZonedDateTime = ZonedDateTimeFactory.now(),
): boolean => {
  const windowEndUnixTime = requestWindow.windowEnd?.unixTimeMillis ?? Number.MAX_VALUE;

  return windowEndUnixTime <= zonedDateTime.unixTimeMillis;
};

const toDeviceModelRefs = ({
  details: { deviceModelReferences },
}: DeviceObservationRequest): DeviceObservationRequestModelRefV1[] =>
  deviceModelReferences.map(DeviceObservationRequestModelRefV1.from);

const toScheduleRequestRefWindowKey = (scheduleRequestRef: DeviceObservationScheduleRequestRefV1): string => {
  const { scheduleCode, versionCode, requestCode, windowStart, windowEnd } = scheduleRequestRef;

  return `${scheduleCode}-${versionCode}-${requestCode}-${windowStart}-${windowEnd}`;
};

export const createRequestWindowFromMessage = (
  message: ShowDeviceMessage,
  windowReferenceTimestamp: ZonedDateTime,
): RequestWindow =>
  RequestWindow.fromRefAndTimingWindow(message.payload.scheduleRef, {
    frequency: 1,
    frequencyMax: 1,
    repeatIndex: 0,
    windowStart: message.validFrom,
    windowReferenceTimestamp,
    windowEnd: message.validTo,
  });

export const DeviceRequestUtil = {
  isNotExpired,
  createRequestWindowFromMessage,
  filterActiveNowRequests,
  filterOnlyVisibleStatuses,
  filterRequestsWithSupportedDeviceModels,
  filterSchedulesRequests,
  filterOnDemandRequests,
  findByScheduleVersionRequestRef,
  isRequestVersionActiveNow,
  isWindowPassed,
  toDeviceModelRefs,
  toScheduleRequestRefWindowKey,
};
